/**
 * ChangePasswordPage — self-service password change.
 *
 * Two entry routes:
 *   1. Forced (mustChangePassword=true) — user lands here from LoginPage
 *      after sign-in. JWT carries pwdChange=true; the back-end refuses
 *      every endpoint except POST /api/auth/me/password until the user
 *      clears the gate.
 *   2. Voluntary — user navigates here from "Account → Change password"
 *      (link added in AppLayout). JWT has no pwdChange claim; the page
 *      behaves the same way.
 *
 * On success → navigate to "/" and reloadMe so the next page render
 * sees the cleared claim.
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ApiError } from "@dfp/api-client";
import { useAuth } from "../auth/AuthContext.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { api } from "../lib/api.js";

export function ChangePasswordPage(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const forced = auth.user?.pwdChange === true;
  const longEnough = newPassword.length >= 12;
  const matches = newPassword.length === 0 || newPassword === confirmPassword;
  const distinct = newPassword !== currentPassword || newPassword.length === 0;
  const submittable =
    !pending && currentPassword.length > 0 && newPassword.length >= 12 && matches && distinct;

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      await api.post<{ ok: true }>("/api/auth/me/password", {
        currentPassword,
        newPassword,
      });
      // The change clears the server's mustChangePassword flag. The gate is
      // enforced authoritatively server-side: PasswordChangeGuard re-checks
      // the LIVE flag, so gated endpoints work immediately even though this
      // access token still carries the now-stale pwdChange claim (it drops on
      // the token's next natural refresh). No client-side token juggling —
      // and no logout risk from a refresh that happens to fail here.
      await auth.reloadMe();
      void navigate({ to: "/" });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Change failed");
      } else {
        setError("Network error");
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="mx-auto max-w-md p-6">
      <Card>
        <CardHeader>
          <CardTitle>{forced ? "Set a new password" : "Change your password"}</CardTitle>
          {forced ? (
            <p className="mt-2 text-xs text-amber-700">
              Your administrator set a temporary password. Choose a new one to continue.
            </p>
          ) : null}
        </CardHeader>
        <CardBody>
          <form onSubmit={onSubmit} className="space-y-4">
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Current password
              </span>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                New password (≥12 chars)
              </span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                maxLength={128}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              {!longEnough && newPassword.length > 0 ? (
                <span className="mt-1 block text-xs text-amber-700">
                  Password must be at least 12 characters.
                </span>
              ) : null}
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Confirm new password
              </span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                maxLength={128}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              {!matches ? (
                <span className="mt-1 block text-xs text-amber-700">
                  Passwords don&apos;t match.
                </span>
              ) : null}
            </label>
            {!distinct && newPassword.length > 0 ? (
              <p className="text-xs text-amber-700">New password must differ from current.</p>
            ) : null}
            {error ? <p className="text-xs text-red-700">{error}</p> : null}
            <div className="flex justify-between">
              {!forced ? (
                <Button variant="ghost" type="button" onClick={() => void navigate({ to: "/" })}>
                  Cancel
                </Button>
              ) : (
                <span />
              )}
              <Button type="submit" variant="primary" disabled={!submittable}>
                {pending ? "Saving…" : forced ? "Set new password" : "Change password"}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
